import axios from 'axios';
import { API_URL } from '../config/api';
import { collectAttendanceClientContext } from './attendanceDeviceContext';
import { readAttendanceEventsFromRealm, upsertRealmDocs } from '../realm';
import {
  buildBreakStatusFromEvents,
  computeWorkSessionFromEvents,
  filterEventsForDate,
  officeMinutesFromElapsedSeconds,
  resolveSessionDateFromEvents,
} from '../utils/workSessionUtils';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
};

const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
  } catch {
    return 'Asia/Kolkata';
  }
};

/** Coerce to a single yyyy-MM-dd (axios/Spring can turn duplicate params into "date,date"). */
const asDateParam = (value) => {
  if (value == null || value === '') return undefined;
  const raw = Array.isArray(value) ? String(value[0] ?? '') : String(value);
  const token = raw.split(',')[0].trim();
  return token || undefined;
};

export const fetchAttendanceBriefing = async () => {
  const response = await axios.get(`${API_URL}/attendance/briefing`, {
    params: { timezone: browserTimezone() },
    headers: getHeaders(),
  });
  return response.data;
};

export const fetchAttendanceEventBriefing = async (eventId) => {
  const response = await axios.get(`${API_URL}/attendance/events/${eventId}/briefing`, {
    params: { timezone: browserTimezone() },
    headers: getHeaders(),
  });
  return response.data;
};

export const checkInAttendance = async () => {
  invalidateBreakStatusCache();
  const response = await axios.post(
    `${API_URL}/attendance/check-in`,
    { timezone: browserTimezone(), source: 'WEB' },
    { headers: getHeaders() },
  );
  return response.data;
};

export const confirmAttendanceCheckIn = async (workLocation) => {
  invalidateBreakStatusCache();
  const clientContext = await collectAttendanceClientContext();
  const response = await axios.post(
    `${API_URL}/attendance/check-in/confirm`,
    {
      timezone: browserTimezone(),
      source: 'WEB',
      ...clientContext,
      ...(workLocation ? { workLocation } : {}),
    },
    { headers: getHeaders() },
  );
  return response.data;
};

export const fetchLogoutSummary = async () => {
  const response = await axios.get(`${API_URL}/attendance/logout/summary`, {
    params: { timezone: browserTimezone() },
    headers: getHeaders(),
  });
  return response.data;
};

export const fetchAttendanceEventLogoutSummary = async (eventId) => {
  const response = await axios.get(`${API_URL}/attendance/events/${eventId}/logout-summary`, {
    params: { timezone: browserTimezone() },
    headers: getHeaders(),
  });
  return response.data;
};

export const confirmAttendanceLogout = async (comment = '') => {
  invalidateBreakStatusCache();
  const clientContext = await collectAttendanceClientContext();
  const response = await axios.post(
    `${API_URL}/attendance/logout/confirm`,
    { timezone: browserTimezone(), source: 'WEB', comment, ...clientContext },
    { headers: getHeaders() },
  );
  return response.data;
};

export const fetchLoginDays = async (startDate, endDate, ownerUserId) => {
  const response = await axios.get(`${API_URL}/attendance/login-days`, {
    params: {
      startDate: asDateParam(startDate),
      endDate: asDateParam(endDate),
      timezone: browserTimezone(),
      ownerUserId: ownerUserId || undefined,
    },
    headers: getHeaders(),
  });
  const dates = response.data?.dates;
  return Array.isArray(dates) ? dates : [];
};

export const fetchDayActivityTimeline = async (date, ownerUserId) => {
  const day = asDateParam(date) || new Date().toLocaleDateString('en-CA');
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

  // Prefer Realm for the current user's own day when offline / cold start
  if (!ownerUserId) {
    try {
      const cached = await readAttendanceEventsFromRealm();
      if (cached && cached.length) {
        const events = filterEventsForDate(cached, day)
          .sort((a, b) => String(a.eventAt || '').localeCompare(String(b.eventAt || '')));
        if (events.length) {
          if (!offline) {
            axios
              .get(`${API_URL}/attendance/day-timeline`, {
                params: {
                  timezone: browserTimezone(),
                  date: day,
                },
                headers: getHeaders(),
              })
              .then((response) => {
                const list = response.data?.events || response.data?.attendanceEvents || [];
                if (Array.isArray(list) && list.length) {
                  upsertRealmDocs('AttendanceEvent', list);
                }
              })
              .catch(() => {});
          }
          const session = computeWorkSessionFromEvents(events);
          const workingTimeMinutes = officeMinutesFromElapsedSeconds(session.elapsedSeconds);
          const entries = events.map((e) => ({
            eventId: e.id || e._id,
            type: e.type,
            title: e.type,
            subtitle: e.comment || e.reason || e.note || '',
            timeLabel: e.eventAt || '',
            deviceLabel: e.deviceLabel || '',
            locationLabel: e.locationLabel || '',
            eventAt: e.eventAt || '',
          }));
          return {
            date: day,
            dateLabel: day,
            dayLabel: day,
            entries,
            activeDay: session.checkedIn && !session.loggedOut,
            workingTimeMinutes,
            workingTime: `${workingTimeMinutes}m`,
            totalTimeMinutes: workingTimeMinutes,
            totalTime: `${workingTimeMinutes}m`,
            fromRealm: true,
          };
        }
      }
    } catch {
      // fall through
    }
  }

  if (offline && !ownerUserId) {
    return {
      date: day,
      dateLabel: day,
      dayLabel: day,
      entries: [],
      activeDay: false,
      workingTimeMinutes: 0,
      workingTime: '0m',
      fromRealm: true,
    };
  }

  const response = await axios.get(`${API_URL}/attendance/day-timeline`, {
    params: {
      timezone: browserTimezone(),
      date: day,
      ownerUserId: ownerUserId || undefined,
    },
    headers: getHeaders(),
  });
  if (!ownerUserId && Array.isArray(response.data?.entries)) {
    // Persist raw-ish entries keyed as AttendanceEvent when possible
    const asEvents = response.data.entries
      .filter((e) => e.eventId)
      .map((e) => ({
        id: e.eventId,
        _id: e.eventId,
        type: e.type,
        eventAt: e.eventAt,
        date: response.data.date || day,
        deviceLabel: e.deviceLabel,
        locationLabel: e.locationLabel,
      }));
    if (asEvents.length) {
      upsertRealmDocs('AttendanceEvent', asEvents);
    }
  }
  return response.data;
};

let breakStatusInFlight = null;
let breakStatusCache = null;
let breakStatusCacheAt = 0;
const BREAK_STATUS_CACHE_MS = 2500;

/** Invalidate cached break status after check-in / break / logout mutations. */
export const invalidateBreakStatusCache = () => {
  breakStatusCache = null;
  breakStatusCacheAt = 0;
  breakStatusInFlight = null;
};

export const fetchBreakStatus = async ({ force = false } = {}) => {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const buildLocal = async () => {
    const cached = await readAttendanceEventsFromRealm();
    if (!cached || !cached.length) return null;
    const calendarToday = new Date().toLocaleDateString('en-CA');
    const sessionDate = resolveSessionDateFromEvents(cached, calendarToday);
    return buildBreakStatusFromEvents(cached, {
      dateStr: sessionDate,
      calendarDate: calendarToday,
    });
  };

  if (offline) {
    const local = await buildLocal();
    if (local) return local;
    return {
      checkedIn: false,
      loggedOut: false,
      onBreak: false,
      workRunning: false,
      workAccumulatedSeconds: 0,
      workElapsedSeconds: 0,
      fromLocal: true,
    };
  }

  const now = Date.now();
  if (force) {
    breakStatusCache = null;
    breakStatusCacheAt = 0;
  } else if (breakStatusCache && (now - breakStatusCacheAt) < BREAK_STATUS_CACHE_MS) {
    return breakStatusCache;
  }
  if (breakStatusInFlight) {
    return breakStatusInFlight;
  }

  breakStatusInFlight = (async () => {
    try {
      const response = await axios.get(`${API_URL}/attendance/break/status`, {
        params: { timezone: browserTimezone() },
        headers: getHeaders(),
      });
      breakStatusCache = response.data;
      breakStatusCacheAt = Date.now();
      return response.data;
    } catch (err) {
      const local = await buildLocal();
      if (local) return local;
      throw err;
    } finally {
      breakStatusInFlight = null;
    }
  })();

  return breakStatusInFlight;
};

export const startShortBreak = async (reason) => {
  invalidateBreakStatusCache();
  const response = await axios.post(
    `${API_URL}/attendance/break/start`,
    { timezone: browserTimezone(), source: 'WEB', reason },
    { headers: getHeaders() },
  );
  return response.data;
};

export const endShortBreak = async () => {
  invalidateBreakStatusCache();
  const response = await axios.post(
    `${API_URL}/attendance/break/end`,
    { timezone: browserTimezone(), source: 'WEB' },
    { headers: getHeaders() },
  );
  return response.data;
};

const callSessionBody = (extra = {}) => ({
  timezone: browserTimezone(),
  source: 'WEB',
  ...extra,
});

export const recordCallJoin = async ({
  title,
  channelId,
  messageId,
  roomName,
  provider,
  liveSession = true,
} = {}) => {
  const response = await axios.post(
    `${API_URL}/attendance/call/join`,
    callSessionBody({
      title: title || 'Direct call',
      channelId: channelId || undefined,
      messageId: messageId || undefined,
      roomName: roomName || undefined,
      provider: provider || 'livekit',
      liveSession: liveSession ? 'true' : 'false',
    }),
    { headers: getHeaders() },
  );
  return response.data;
};

export const recordCallLeave = async ({ keepalive = false } = {}) => {
  const payload = callSessionBody();
  if (keepalive) {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      fetch(`${API_URL}/attendance/call/leave`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // ignore
    }
    return null;
  }
  const response = await axios.post(
    `${API_URL}/attendance/call/leave`,
    payload,
    { headers: getHeaders() },
  );
  return response.data;
};

export const reloginAttendance = async (workLocation) => {
  invalidateBreakStatusCache();
  const response = await axios.post(
    `${API_URL}/attendance/relogin`,
    {
      timezone: browserTimezone(),
      source: 'WEB',
      ...(workLocation ? { workLocation } : {}),
    },
    { headers: getHeaders() },
  );
  return response.data;
};

export const applyLeave = async ({ leaveType, reason, startTime = null, endTime = null }) => {
  const response = await axios.post(
    `${API_URL}/attendance/leave/apply`,
    {
      timezone: browserTimezone(),
      source: 'WEB',
      leaveType,
      reason,
      ...(startTime ? { startTime } : {}),
      ...(endTime ? { endTime } : {}),
    },
    { headers: getHeaders() },
  );
  return response.data;
};

export const fetchLeaveStatus = async () => {
  const response = await axios.get(`${API_URL}/attendance/leave/status`, {
    params: { timezone: browserTimezone() },
    headers: getHeaders(),
  });
  return response.data;
};

export const fetchMyLeaves = async () => {
  const response = await axios.get(`${API_URL}/attendance/leave/mine`, {
    params: { timezone: browserTimezone() },
    headers: getHeaders(),
  });
  return response.data;
};

export const cancelLeave = async (leaveId) => {
  const body = { timezone: browserTimezone(), source: 'WEB' };
  if (leaveId) body.leaveId = leaveId;
  const response = await axios.post(
    `${API_URL}/attendance/leave/cancel`,
    body,
    { headers: getHeaders() },
  );
  return response.data;
};

export const requestLeave = async (fromDate, toDate, reason) => {
  const response = await axios.post(
    `${API_URL}/attendance/leave/request`,
    { timezone: browserTimezone(), source: 'WEB', fromDate, toDate, reason },
    { headers: getHeaders() },
  );
  return response.data;
};

export const fetchPendingLeaveApprovals = async () => {
  const response = await axios.get(`${API_URL}/attendance/leave/pending-approvals`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const approveLeave = async (leaveId) => {
  const response = await axios.post(
    `${API_URL}/attendance/leave/${leaveId}/approve`,
    { timezone: browserTimezone(), source: 'WEB' },
    { headers: getHeaders() },
  );
  return response.data;
};

export const rejectLeave = async (leaveId) => {
  const response = await axios.post(
    `${API_URL}/attendance/leave/${leaveId}/reject`,
    { timezone: browserTimezone(), source: 'WEB' },
    { headers: getHeaders() },
  );
  return response.data;
};

export const isAttendanceChannel = (channel) =>
  channel?.type === 'ATTENDANCE'
  || channel?.systemChannelKey === 'ATTENDANCE'
  || (channel?.name || '').toLowerCase() === 'attendance';

/** Attendance timeline shows only the current user's check-in / logout entries. */
export const isMyAttendanceTimelineMessage = (message, userId) => {
  if (!message || !userId) return false;
  const eventUserId = message.attendanceInfo?.eventUserId;
  if (eventUserId != null && eventUserId !== '') {
    return String(eventUserId) === String(userId);
  }
  return String(message.senderId) === String(userId);
};

export const grantAttendanceShare = async (viewerUserIds) => {
  const body = Array.isArray(viewerUserIds)
    ? { viewerUserIds }
    : { viewerUserId: viewerUserIds };
  const response = await axios.post(`${API_URL}/attendance/share/grant`, body, {
    headers: getHeaders(),
  });
  return response.data;
};

export const fetchSharedWithMe = async () => {
  const response = await axios.get(`${API_URL}/attendance/share/shared-with-me`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const startSharedUserBreak = async (userId, reason) => {
  const response = await axios.post(
    `${API_URL}/attendance/team/break/start`,
    { userId, reason, timezone: browserTimezone(), source: 'WEB' },
    { headers: getHeaders() },
  );
  return response.data;
};

export const endSharedUserBreak = async (userId) => {
  const response = await axios.post(
    `${API_URL}/attendance/team/break/end`,
    { userId, timezone: browserTimezone(), source: 'WEB' },
    { headers: getHeaders() },
  );
  return response.data;
};

export const markSharedUserOnLeave = async ({
  userId,
  leaveType = 'FULL_DAY',
  markKind,
  reason,
  startTime,
  endTime,
  date,
}) => {
  const response = await axios.post(
    `${API_URL}/attendance/team/leave/mark`,
    {
      userId,
      leaveType,
      timezone: browserTimezone(),
      source: 'WEB',
      ...(markKind ? { markKind } : {}),
      ...(reason ? { reason } : {}),
      ...(startTime ? { startTime } : {}),
      ...(endTime ? { endTime } : {}),
      ...(date ? { date } : {}),
    },
    { headers: getHeaders() },
  );
  return response.data;
};

export const revertSharedUserLeave = async (userId, date) => {
  const response = await axios.post(
    `${API_URL}/attendance/team/leave/revert`,
    {
      userId,
      timezone: browserTimezone(),
      source: 'WEB',
      ...(date ? { date } : {}),
    },
    { headers: getHeaders() },
  );
  return response.data;
};

export const fetchTeamLiveStatus = async () => {
  const response = await axios.get(`${API_URL}/attendance/team/live-status`, {
    params: { timezone: browserTimezone() },
    headers: getHeaders(),
  });
  return response.data;
};

export const fetchSharedByMe = async () => {
  const response = await axios.get(`${API_URL}/attendance/share/shared-by-me`, {
    headers: getHeaders(),
  });
  return response.data;
};

export const revokeAttendanceShare = async (shareId) => {
  const response = await axios.put(`${API_URL}/attendance/share/${shareId}/revoke`, {}, {
    headers: getHeaders(),
  });
  return response.data;
};

export const fetchTeamAttendanceProgress = async (userId, startDate, endDate) => {
  const response = await axios.get(`${API_URL}/attendance/team/progress`, {
    params: {
      userId,
      startDate: asDateParam(startDate),
      endDate: asDateParam(endDate),
      timezone: browserTimezone(),
    },
    headers: getHeaders(),
  });
  return response.data;
};

export const fetchTeamAttendanceAnalytics = async (userId, startDate, endDate) => {
  const response = await axios.get(`${API_URL}/attendance/team/analytics`, {
    params: {
      userId,
      startDate: asDateParam(startDate),
      endDate: asDateParam(endDate),
      timezone: browserTimezone(),
    },
    headers: getHeaders(),
  });
  return response.data;
};

export const fetchMemberSummary = async (userId, startDate, endDate) => {
  const response = await axios.get(`${API_URL}/attendance/team/members/${userId}/summary`, {
    params: {
      timezone: browserTimezone(),
      startDate: asDateParam(startDate),
      endDate: asDateParam(endDate),
    },
    headers: getHeaders(),
  });
  return response.data;
};

export const saveMemberSupervisorNotes = async (userId, body, date, startDate, endDate) => {
  const response = await axios.put(
    `${API_URL}/attendance/team/members/${userId}/notes`,
    { body: body || '', date },
    {
      params: {
        timezone: browserTimezone(),
        startDate: asDateParam(startDate),
        endDate: asDateParam(endDate),
      },
      headers: getHeaders(),
    },
  );
  return response.data;
};
